import { DateTime } from "luxon";

import * as types from "types";
import {
  DEFAULT_SYSTEM_PROMPT,
  MCP_TASKS_GUIDELINES,
} from "types/personas/defaultSystemPrompt";
import { getCodeExecutionSystemMessage } from "utilities/codeExecutionMcp";
import { getSubagentSystemMessage } from "utilities/subagentMcp";

/**
 * Prepends a system message to the beginning of the messages array.
 * This message includes information about the assistant and how to use it.
 * @param messages - The messages array to prepend to.
 * @param persona - The persona to use for the system message.
 * @param enableSubagent - Whether subagent delegation is enabled via the toggle.
 * @param incognito - When true, suppress tool-related sections regardless of
 *   toggle state. The request builder strips the corresponding MCPs in
 *   incognito mode (ADR 057), so telling the model it has those tools would
 *   invite confused refusals.
 * @returns The messages array with the system message prepended.
 */
export function prependSystemPrompt(
  messages: types.Message[],
  persona: types.Persona,
  enableSubagent = false,
  incognito = false
): types.Message[] {
  const today = DateTime.now().toLocaleString(DateTime.DATE_FULL);
  let systemMessageText = persona.systemMessage ?? DEFAULT_SYSTEM_PROMPT;

  if (!incognito && persona.codeExecutionCapable !== false) {
    systemMessageText += getCodeExecutionSystemMessage();
  }

  // Inject subagent instructions only when the toggle is enabled
  if (!incognito && enableSubagent && persona.subagentCapable === true) {
    systemMessageText += getSubagentSystemMessage();
  }

  systemMessageText += `\n\n${MCP_TASKS_GUIDELINES}`;

  // Always append current date no matter what
  systemMessageText += `\n\nThe current date is ${today}.`;

  const systemMessage: types.SystemMessage = {
    uuid: crypto.randomUUID(),
    role: "system",
    content: systemMessageText,
    hide: true,
  };

  return [systemMessage, ...messages];
}
